import { stringify } from 'qs';
import { useEffect, useRef } from 'react';
import type { QueryKey } from '@tanstack/react-query';

import { queryClient, useQuery } from '../../../ReactQuery';
import { get } from '../../../redux/api';
import type { FossabotPRStatus } from '../../../../shared/fossabot/common';
import type { FossabotPRStatusResponse } from '../../../../shared/fossabot/response';

import { getFossabotPRsQueryKeyPrefix } from './useFossabotPRCounts';

/*
 * How often to poll per status: fast while fossabot is actively moving the PR
 * forward, slower while it sits open on GitHub so a merge or close done there
 * still shows up. Settled statuses are absent, so they do not poll at all.
 */
const POLL_INTERVAL_MS: Partial<Record<FossabotPRStatus, number>> = {
  creating: 3000,
  analyzing: 3000,
  needsReview: 15000,
  readyToMerge: 15000,
};

type FossabotPRStatusOptions = {
  /** Required only when the issue affects more than one project. */
  projectLocator?: string;
  /** Creation-job id from the create mutation; a failed job is only reportable through its id. */
  jobId?: string;
};

function fetchFossabotPRStatus(issueId: number, { projectLocator, jobId }: FossabotPRStatusOptions) {
  /*
   * qs skips undefined values and omits the '?' when nothing is left, so an
   * issue with no locator or job just gets the bare URL. It also encodes by
   * default, which project locators need: they contain slashes and '+'.
   */
  const queryString = stringify({ projectLocator, jobId }, { addQueryPrefix: true });
  return get<FossabotPRStatusResponse>(`/api/fossabot/issues/${issueId}/dependency-upgrade-pr${queryString}`);
}

/**
 * Prefix shared by every fossabot query for an issue; invalidating it
 * refetches the issue's PR status regardless of the options used.
 */
export const getFossabotIssueQueryKey = (issueId: number | null): QueryKey => ['fossabot', 'issues', issueId];

export const getFossabotPRStatusQueryKey = (
  issueId: number | null,
  { projectLocator, jobId }: FossabotPRStatusOptions = {},
): QueryKey => [
  ...getFossabotIssueQueryKey(issueId),
  'dependency-upgrade-pr',
  { projectLocator: projectLocator ?? null, jobId: jobId ?? null },
];

/**
 * Query for the current fossabot dependency-upgrade PR state of an issue.
 *
 * A status of 'none' means the issue has no PR yet. The PR lifecycle advances
 * asynchronously on the backend, so this polls fast while the PR is being
 * created or analyzed, slowly while it awaits review or merge on GitHub, and
 * not at all once the status settles (none, merged, closed, failed).
 *
 * @param {number | null} issueId - The FOSSA issue to report on, or null while
 *   the caller has no issue yet, which disables the query.
 * @param {FossabotPRStatusOptions} options - Optional projectLocator and jobId query params.
 * @param {boolean} shouldFetch - Will not fetch if false.
 */
export function useFossabotPRStatus(
  issueId: number | null,
  options: FossabotPRStatusOptions = {},
  shouldFetch = true,
) {
  const query = useQuery<FossabotPRStatusResponse>({
    queryKey: getFossabotPRStatusQueryKey(issueId, options),
    // Guarded by enabled below, which keeps the query off until there is an id.
    queryFn: () => fetchFossabotPRStatus(issueId as number, options),
    enabled: shouldFetch && issueId !== null,
    /*
     * The jobId option is part of the query key, and it changes mid-flow: it is
     * set once the create mutation returns. Keep showing the previous status
     * across that key change instead of flashing back to a loading state.
     */
    keepPreviousData: true,
    refetchInterval: (data) => (data && POLL_INTERVAL_MS[data.status]) ?? false,
  });

  /*
   * The PR advances on the backend while this polls, so a status change seen
   * here (created, merged, closed) is exactly what makes the project's PR
   * counts (subnav badge, stat cards) and list stale. Refetch them on every
   * observed transition; the first observed status is not one.
   */
  const previousStatus = useRef<FossabotPRStatus | null>(null);
  const status = query.data?.status ?? null;
  useEffect(() => {
    if (previousStatus.current !== null && status !== null && status !== previousStatus.current) {
      queryClient.invalidateQueries({ queryKey: getFossabotPRsQueryKeyPrefix() });
    }
    if (status !== null) {
      previousStatus.current = status;
    }
  }, [status]);

  return query;
}
